import {useAuth} from '../auth/use-auth';
import {useSettings} from '@ui/settings/use-settings';
import dot from 'dot-object';
import {useMemo} from 'react';
import {User} from '@ui/types/user';
import {
  MenuConfig,
  MenuItemChildConfig,
  MenuItemConfig,
} from '@common/menus/menu-config';

export function useCustomMenu(menuOrPosition?: string | MenuConfig) {
  const settings = useSettings();
  const {user, hasPermission} = useAuth();

  return useMemo(() => {
    if (!menuOrPosition) {
      return null;
    }

    const menu =
      typeof menuOrPosition === 'string'
        ? settings.menus?.find(s => s.positions?.includes(menuOrPosition))
        : menuOrPosition;

    const menuItems: MenuItemConfig[] = [];

    if (menu) {
      for (const item of menu.items) {
        const hasRoles = (item.roles || []).every(
          a => user?.roles.find(b => b.id === a),
        );
        const hasPermissions = (item.permissions || []).every(a =>
          hasPermission(a),
        );
        const hasSettings =
          !item.settings ||
          Object.entries(item.settings).every(([key, value]) => {
            return dot.pick(key, settings) == value;
          });

        if (
          item.type !== 'dropdown' &&
          (!item.action ||
            !hasRoles ||
            !hasPermissions ||
            !hasSettings ||
            !subscriptionStatusMatches(item, user))
        ) {
          continue;
        }

        if (item.type === 'dropdown') {
          const visibleChildren = (item.children || [])
            .filter(child => {
              const childHasRoles = (child.roles || []).every(a =>
                user?.roles.find(b => b.id === a),
              );

              const childHasPermissions = (child.permissions || []).every(a =>
                hasPermission(a),
              );

              const childHasSettings =
                !child.settings ||
                Object.entries(child.settings).every(([key, value]) => {
                  return dot.pick(key, settings) == value;
                });

              return (
                !!child.action &&
                childHasRoles &&
                childHasPermissions &&
                childHasSettings &&
                subscriptionStatusMatches(child, user)
              );
            })
            .map(child => ({
              ...child,
              action: user?.id
                ? child.action?.replace(/{currentUser}/g, `${user.id}`)
                : child.action,
            }));

          if (!visibleChildren.length) {
            continue;
          }

          menuItems.push({
            ...item,
            children: visibleChildren,
          });

          continue;
        }

        const action = user?.id
          ? item.action?.replace(/{currentUser}/g, `${user.id}`)
          : item.action;

        menuItems.push({
          ...item,
          action,
        });
      }

      menu.items = menuItems;
    }

    return menu;
  }, [hasPermission, settings, menuOrPosition, user]);
}

function subscriptionStatusMatches(
  item: MenuItemConfig | MenuItemChildConfig,
  user?: User | null,
): boolean {
  if (!item.subscriptionStatus) {
    return true;
  }

  const hasActiveSubscription = !!user?.subscriptions?.find(sub => sub.active);

  if (item.subscriptionStatus === 'subscribed') {
    return hasActiveSubscription;
  }

  return !hasActiveSubscription;
}